import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { adminApi } from "@/lib/api/endpoints";
import { useAsync } from "@/lib/hooks/useAsync";
import { useDebouncedEffect } from "@/lib/hooks/useDebouncedEffect";
import { Badge, Button, Card, EmptyState, Input, Modal, Select, useToast } from "@/components/ui";
import { formatDateTime, formatRelative } from "@/lib/utils/format";
import type { GalleryItem } from "@/lib/types/api";
import { IconDownload, IconGallery, IconSearch } from "./components/icons";

/**
 * Every generation the organization has produced.
 *
 * Selection drives the ZIP export, which is how operators hand a client their
 * photos after an event — so selection state has to survive filtering and
 * paging rather than resetting under them.
 */
export function GalleryPage() {
  const toast = useToast();

  const [page, setPage] = useState(1);
  const [eventId, setEventId] = useState("");
  const [provider, setProvider] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [lightbox, setLightbox] = useState<GalleryItem | null>(null);
  const [exporting, setExporting] = useState(false);

  useDebouncedEffect(() => { setSearch(searchInput); setPage(1); }, 350, [searchInput]);

  const gallery = useAsync(
    () => adminApi.gallery({ page, per_page: 24, event_id: eventId, provider, search }),
    [page, eventId, provider, search],
  );

  // Filters change the result set; page 1 is the only safe place to land.
  useEffect(() => { setPage(1); }, [eventId, provider]);

  const items = gallery.data?.generations ?? [];
  const pagination = gallery.data?.pagination;

  const allOnPageSelected = useMemo(
    () => items.length > 0 && items.every((item) => selected.has(item.id)),
    [items, selected],
  );

  function toggle(id: number) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function togglePage() {
    setSelected((current) => {
      const next = new Set(current);
      if (allOnPageSelected) items.forEach((item) => next.delete(item.id));
      else items.forEach((item) => next.add(item.id));
      return next;
    });
  }

  async function exportSelection() {
    setExporting(true);
    try {
      if (selected.size > 0) {
        await adminApi.exportGenerations({ generation_ids: [...selected] });
      } else if (eventId) {
        await adminApi.exportGenerations({ event_id: Number(eventId) });
      } else {
        // The API requires a scope; exporting an entire organization would be
        // an accidental multi-gigabyte download.
        toast.error("Select some images, or filter to one event, before exporting.");
        return;
      }
      toast.success("Export started — your download will begin shortly.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Export failed");
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="label-eyebrow text-accent/80">Library</span>
          <h1 className="text-xl font-semibold">AI Gallery</h1>
          <p className="text-sm text-haze-300">
            {pagination ? `${pagination.total_count.toLocaleString()} generations` : "Loading…"}
            {selected.size > 0 && ` · ${selected.size} selected`}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {selected.size > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Clear selection</Button>
          )}
          <Button variant="primary" size="sm" loading={exporting} icon={<IconDownload size={14} />} onClick={exportSelection}>
            Export {selected.size > 0 ? `${selected.size} as ZIP` : "as ZIP"}
          </Button>
        </div>
      </header>

      <Card className="flex flex-wrap items-center gap-3 p-4">
        <div className="relative min-w-[220px] flex-1">
          <IconSearch size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-haze-400" />
          <Input
            placeholder="Search prompts…"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={eventId} onChange={(event) => setEventId(event.target.value)} className="w-auto min-w-[180px]">
          <option value="">All events</option>
          {gallery.data?.events.map((event) => (
            <option key={event.id} value={event.id}>{event.name}</option>
          ))}
        </Select>

        <Select value={provider} onChange={(event) => setProvider(event.target.value)} className="w-auto min-w-[150px]">
          <option value="">All providers</option>
          {gallery.data?.providers.map((name) => <option key={name} value={name}>{name}</option>)}
        </Select>

        {items.length > 0 && (
          <Button variant="ghost" size="sm" onClick={togglePage}>
            {allOnPageSelected ? "Deselect page" : "Select page"}
          </Button>
        )}
      </Card>

      {gallery.loading ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-6">
          {Array.from({ length: 12 }).map((_, index) => <div key={index} className="skeleton aspect-square" />)}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<IconGallery size={28} />}
          title="Nothing matches those filters"
          description="Try clearing the search or picking a different event."
        />
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-6">
          {items.map((item) => (
            <GalleryTile
              key={item.id}
              item={item}
              selected={selected.has(item.id)}
              onToggle={() => toggle(item.id)}
              onOpen={() => setLightbox(item)}
            />
          ))}
        </div>
      )}

      {pagination && pagination.total_pages > 1 && (
        <nav className="flex items-center justify-center gap-3">
          <Button size="sm" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</Button>
          <span className="text-sm text-haze-400">Page {pagination.current_page} of {pagination.total_pages}</span>
          <Button size="sm" disabled={page >= pagination.total_pages} onClick={() => setPage((value) => value + 1)}>Next</Button>
        </nav>
      )}

      <Modal open={Boolean(lightbox)} onClose={() => setLightbox(null)} title={lightbox?.event_name ?? ""} size="lg">
        {lightbox && (
          <div className="flex flex-col gap-4">
            {lightbox.image_url && (
              <img src={lightbox.image_url} alt="" className="max-h-[55vh] w-full rounded-lg object-contain" />
            )}
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <Detail label="Generated">{formatDateTime(lightbox.created_at)}</Detail>
              <Detail label="Provider">{lightbox.provider ?? "—"}</Detail>
              <div className="sm:col-span-2">
                <dt className="label-eyebrow mb-1">Prompt used</dt>
                <dd className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-lg bg-ink-800 p-3 text-xs leading-relaxed text-haze-300">
                  {lightbox.prompt_used ?? "—"}
                </dd>
              </div>
            </dl>
            {lightbox.image_url && (
              <a
                href={lightbox.image_url}
                download
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-accent px-4 text-sm font-semibold text-accent-ink hover:bg-accent-bright"
              >
                <IconDownload size={15} /> Download original
              </a>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="label-eyebrow mb-1">{label}</dt>
      <dd className="text-haze-200">{children}</dd>
    </div>
  );
}

function GalleryTile({
  item, selected, onToggle, onOpen,
}: { item: GalleryItem; selected: boolean; onToggle: () => void; onOpen: () => void }) {
  return (
    <figure
      className={[
        "group relative aspect-square overflow-hidden rounded-lg border bg-ink-800 transition-colors",
        selected ? "border-accent" : "border-ink-600 hover:border-ink-500",
      ].join(" ")}
    >
      {item.image_url ? (
        <img
          src={item.image_url}
          alt=""
          loading="lazy"
          onClick={onOpen}
          className="h-full w-full cursor-zoom-in object-cover"
        />
      ) : (
        <div className="grid h-full place-items-center text-xs text-haze-400">No image</div>
      )}

      <button
        onClick={onToggle}
        aria-label={selected ? "Deselect" : "Select"}
        className={[
          "absolute left-2 top-2 grid h-6 w-6 place-items-center rounded border transition-all",
          selected
            ? "border-accent bg-accent text-accent-ink"
            : "border-white/30 bg-black/40 text-transparent opacity-0 group-hover:opacity-100",
        ].join(" ")}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M2.5 6.5l2.5 2.5 4.5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-black/85 to-transparent p-2 pt-6 opacity-0 transition-opacity group-hover:opacity-100">
        <span className="truncate text-2xs text-white/90">{item.event_name}</span>
        <Badge tone="neutral">{formatRelative(item.created_at)}</Badge>
      </figcaption>
    </figure>
  );
}
